import React from 'react';
import { TFunction } from 'i18next';
import classNames from 'classnames';
import _get from 'lodash/get';
import _without from 'lodash/without';
import { v4 as uuidv4 } from 'uuid';
import {
  FormAnswerRespGroupEnum,
  FormTemplateQuestionFieldOption,
  FormTemplateQuestionFieldOptionGroupEnum,
  FormTemplateQuestionFieldsKindEnum,
} from 'graphql-common';
import {
  BaseItem,
  Column,
  CellProps,
  CellContentProps,
} from '@lib/components/Table/types';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import CollapseCell from '@lib/components/Table/commonComponents/CollapseCell/CollapseCell';
import TextCellWithIcon from '@lib/components/Table/commonComponents/TextCell/TextCellWithIcon';
import DocumentResponseCell from '@lib/components/Table/commonComponents/DocumentResponseCell/DocumentResponseCell';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import Rating from '@lib/components/Rating/Rating';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import AttachmentListPreview from '@lib/components/AttachmentListPreview/AttachmentListPreview';
import { getValidityHintChipIcon } from '@lib/utils/validityHint';
import formatDecimalString from '@lib/utils/formatDecimalString';
import Typography from '@lib/components/Typography/Typography';
import getCropColorByValidityHint from './getCropColorByValidityHint';
import { TaskCompletionDetailsTableRow } from '../types';
import styles from './TaskCompletionDetailsTableColumns.module.scss';

export type ResponseCellArgs = {
  t: TFunction<'translation', undefined>;
  readingDurationByDocumentationId?: Map<string, number>;
  onDocumentClick?: (documentationId: string) => void;
  isViewLocked?: boolean;
  isFullAdmin?: boolean;
  onUpgradePlan?: () => void;
  // Lets compare mode substitute the row's worst validity hint (across every
  // compared completion) for this cell's crop color, instead of just this
  // column's own answer.
  validityHintOverride?: string | null;
};

/**
 * Renders a single "response" cell body from a `props.item` field row.
 * Extracted so the compare-mode table can reuse the exact same per-kind
 * rendering for each compared completion's column, by substituting `item`
 * (and leaving `disabled`/`onClick`/etc. from the ambient cell props).
 */
export function getResponseCellNode(
  props: CellProps<TaskCompletionDetailsTableRow>,
  {
    t,
    readingDurationByDocumentationId,
    onDocumentClick,
    isViewLocked,
    isFullAdmin,
    onUpgradePlan,
    validityHintOverride,
  }: ResponseCellArgs,
) {
  const { item } = props;
  const { kind } = item;
  const resp = _get(item, 'answer.resp', []);
  const options = (item.options || []) as FormTemplateQuestionFieldOption[];
  const cropColor = getCropColorByValidityHint(
    validityHintOverride ?? item.answer?.validityHint,
  );

  if (
    [
      FormTemplateQuestionFieldsKindEnum.Input,
      FormTemplateQuestionFieldsKindEnum.TextArea,
    ].includes(kind)
  ) {
    const value = _get(resp, ['0', 'value']);
    if (!value) return <TextCell {...props} value="-" />;
    return (
      <CollapseCell
        {...props}
        value={<Typography variant="caption">{value}</Typography>}
        minHeight={20}
        cropColor={cropColor}
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.NumberInput) {
    const optionValue = _get(resp, ['0', 'value']);
    const validityHint = _get(resp, ['0', 'validityHint']);
    if (
      optionValue === undefined ||
      optionValue === null ||
      optionValue === ''
    ) {
      return <TextCell {...props} value="-" />;
    }
    return (
      <TextCellWithIcon
        {...props}
        value={formatDecimalString(optionValue)}
        icon={getValidityHintChipIcon(validityHint)}
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.YesNo) {
    const yesResp = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.Yes,
    );
    const noResp = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.No,
    );
    let icon;
    let value = '-';
    if (_get(yesResp, 'selected')) {
      icon = getValidityHintChipIcon(_get(yesResp, 'validityHint'));
      value = t('yes');
    }
    if (_get(noResp, 'selected')) {
      icon = getValidityHintChipIcon(_get(noResp, 'validityHint'));
      value = t('no');
    }
    return <TextCellWithIcon {...props} value={value} icon={icon} />;
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.MinMax) {
    const minResp = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.Min,
    );
    const maxResp = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.Max,
    );
    const minValue = _get(minResp, 'value');
    const maxValue = _get(maxResp, 'value');
    if (!minValue && !maxValue) {
      return <TextCell {...props} value="-" />;
    }
    return (
      <div className={styles.optionsList}>
        <TextCellWithIcon
          {...props}
          icon={getValidityHintChipIcon(_get(minResp, 'validityHint'))}
          value={
            <span>
              <span className={styles.grayText}>{t('min')}:</span>{' '}
              {minValue ? formatDecimalString(minValue) : '-'}
            </span>
          }
          block
        />
        <TextCellWithIcon
          {...props}
          icon={getValidityHintChipIcon(_get(maxResp, 'validityHint'))}
          value={
            <span>
              <span className={styles.grayText}>{t('max')}:</span>{' '}
              {maxValue ? formatDecimalString(maxValue) : '-'}
            </span>
          }
          block
        />
      </div>
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Date) {
    const value = _get(resp, ['0', 'value']);
    const formattedValue = value ? getFormattedDate(value) : undefined;
    return <TextCell {...props} value={formattedValue ?? '-'} />;
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.DateRange) {
    const startValueRaw = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.StartDate,
    )?.value;
    const endValueRaw = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.EndDate,
    )?.value;
    const startValue = startValueRaw
      ? getFormattedDate(startValueRaw)
      : undefined;
    const endValue = endValueRaw ? getFormattedDate(endValueRaw) : undefined;
    if (!startValue && !endValue) {
      return <TextCell {...props} value="-" />;
    }
    return (
      <TextCell
        {...props}
        value={
          <span className={styles.optionRow}>
            <span className={styles.optionRow}>
              <span className={styles.grayText}>{t('start-date')}:</span>
              {startValue ?? '-'}
            </span>
            <span className={styles.optionRow}>
              <span className={styles.grayText}>{t('end-date')}:</span>
              {endValue ?? '-'}
            </span>
          </span>
        }
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Time) {
    const value = _get(resp, ['0', 'value']);
    return <TextCell {...props} value={value ?? '-'} />;
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.TimeRange) {
    const startTime = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.StartTime,
    )?.value;
    const endTime = resp.find(
      ({ group }) => group === FormAnswerRespGroupEnum.EndTime,
    )?.value;
    if (!startTime && !endTime) {
      return <TextCell {...props} value="-" />;
    }
    return (
      <TextCell
        {...props}
        value={
          <span className={styles.optionRow}>
            <span className={styles.optionRow}>
              <span className={styles.grayText}>{t('start-time')}:</span>
              {startTime ?? '-'}
            </span>
            <span className={styles.optionRow}>
              <span className={styles.grayText}>{t('end-time')}:</span>
              {endTime ?? '-'}
            </span>
          </span>
        }
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Datetime) {
    const value = _get(resp, ['0', 'value']);
    const formattedValue = value
      ? getFormattedDate(value, dateTimeFormatEU)
      : undefined;
    return <TextCell {...props} value={formattedValue ?? '-'} />;
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Rating) {
    const value = _get(resp, ['0', 'value']);
    if (!value) {
      return <TextCell {...props} value="-" />;
    }
    return (
      <Rating
        value={{
          value: parseInt(value, 10),
          label: value,
        }}
        disabledHover
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Attachment) {
    const attachments = item.answer?.attachmentsAttached;
    if (!Array.isArray(attachments) || !attachments.length) {
      return <TextCell {...props} value="-" />;
    }
    return <AttachmentListPreview attachments={attachments} />;
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Checklist) {
    const textValue: string[] = [];
    resp.forEach(({ optionId }) => {
      const option = options.find(({ id }) => id === optionId);
      if (option) {
        const optionValue = _get(option, ['values', 0]);
        if (optionValue) {
          textValue.push(optionValue);
        }
      }
    });
    const value = textValue.join('; ');
    if (!value) {
      return <TextCell {...props} value="-" />;
    }
    return (
      <CollapseCell
        {...props}
        value={value}
        minHeight={20}
        cropColor={cropColor}
      />
    );
  }

  if (
    [
      FormTemplateQuestionFieldsKindEnum.SingleSelect,
      FormTemplateQuestionFieldsKindEnum.Checkbox,
      FormTemplateQuestionFieldsKindEnum.RadioButton,
    ].includes(kind)
  ) {
    const selectedOptions = resp
      .filter(({ selected }) => selected)
      .map((itemResp) => {
        const option = options.find(
          ({ id }) => id === _get(itemResp, 'optionId'),
        );
        return {
          id: _get(itemResp, 'optionId'),
          label: option ? _get(option, ['values', 0]) : undefined,
          validityHint: _get(itemResp, 'validityHint'),
        };
      });

    if (!selectedOptions.length) {
      return <TextCell {...props} value="-" />;
    }

    return (
      <CollapseCell
        {...props}
        minHeight={56}
        cropColor={cropColor}
        value={
          <div>
            {selectedOptions.map(({ id: optionId, label, validityHint }) => (
              <div
                key={optionId ?? uuidv4()}
                className={styles.cellWithValidityHint}
              >
                <TextCellWithIcon
                  {...props}
                  icon={getValidityHintChipIcon(validityHint)}
                  value={label ?? '-'}
                  multiLine
                  block
                />
              </div>
            ))}
          </div>
        }
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.DecisionBox) {
    const selected = resp.find(({ selected: s }) => s);
    if (!selected) {
      return <TextCell {...props} value="-" />;
    }
    return <Chip type={ChipTypes.chipBlue} leftIcon="check_circle" />;
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.CheckboxButtonGrid) {
    const columns = options.filter(
      ({ group }) => group === FormTemplateQuestionFieldOptionGroupEnum.Columns,
    );
    const rows = options.filter(
      ({ group }) => group === FormTemplateQuestionFieldOptionGroupEnum.Rows,
    );

    const hasSelection = resp.some(
      (itemResp) =>
        columns.some(({ id }) => id === _get(itemResp, 'valueId')) &&
        rows.some(({ id }) => id === _get(itemResp, 'optionId')),
    );

    if (!hasSelection) {
      return <TextCell {...props} value="-" />;
    }

    const value = (
      <div>
        {columns.map((columnOption) => {
          const columnValueId = columnOption.id;
          const columnLabel = _get(columnOption, ['values', 0]);
          const columnResponses = resp.filter(
            (columnResp) => _get(columnResp, 'valueId') === columnValueId,
          );
          if (!columnResponses.length) return null;
          return (
            <div key={columnValueId}>
              <div className={classNames(styles.grayText, styles.labelBox)}>
                <TextCell {...props} value={columnLabel ?? '-'} multiLine />
              </div>
              {columnResponses.map((columnResp) => {
                const optionId = _get(columnResp, 'optionId');
                const rowOption = rows.find(({ id }) => id === optionId);
                const rowLabel = rowOption
                  ? _get(rowOption, ['values', 0])
                  : undefined;
                if (!rowLabel) return null;
                return (
                  <div
                    key={`${columnValueId}-${optionId ?? uuidv4()}`}
                    className={styles.cellWithValidityHint}
                  >
                    <TextCellWithIcon
                      {...props}
                      icon={getValidityHintChipIcon(
                        _get(columnResp, 'validityHint'),
                      )}
                      value={rowLabel}
                      multiLine
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );

    return (
      <CollapseCell
        {...props}
        value={value}
        minHeight={44}
        cropColor={cropColor}
      />
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Document) {
    return (
      <DocumentResponseCell
        {...props}
        readingDurationByDocumentationId={readingDurationByDocumentationId}
        onDocumentClick={onDocumentClick}
        isViewLocked={isViewLocked}
        isFullAdmin={isFullAdmin}
        onUpgradePlan={onUpgradePlan}
      />
    );
  }

  const value = _get(resp, ['0', 'value']);
  return <TextCell {...props} value={value ?? '-'} />;
}

type QuestionRow = BaseItem &
  Pick<TaskCompletionDetailsTableRow, 'kind' | 'options' | 'questionName'>;

// Shared by the single-completion and compare tables; `getValidityHint`
// picks the hint the row is coloured by.
export function getValidityHintColumn<T extends BaseItem>(
  getValidityHint: (item: T) => string | null | undefined,
): Column<T> {
  return {
    id: 'validityHint',
    Header: '',
    accessorFn: ({ item }: CellContentProps<T>) =>
      getValidityHint(item) || undefined,
    Cell: ({ value }: CellProps<T>) => getValidityHintChipIcon(value as string),
    width: 60,
    maxWidth: 60,
    minWidth: 60,
  };
}

export function getQuestionColumn<T extends QuestionRow>({
  t,
  getValidityHint,
}: {
  t: TFunction<'translation', undefined>;
  getValidityHint: (item: T) => string | null | undefined;
}): Column<T> {
  return {
    id: 'question',
    Header: t('question'),
    accessor: 'questionName',
    Cell: (props: CellProps<T>) => {
      const { item, value: questionName } = props;
      let value = questionName;
      if (
        item.kind === FormTemplateQuestionFieldsKindEnum.DecisionBox ||
        item.kind === FormTemplateQuestionFieldsKindEnum.ImportantNotice
      ) {
        const label = _get(item, ['options', 0, 'values', 0]);
        value = label ?? value;
      }
      if (!value) return <TextCell {...props} value="-" />;
      return (
        <CollapseCell
          minHeight={20}
          {...props}
          value={<Typography variant="caption">{value}</Typography>}
          cropColor={getCropColorByValidityHint(getValidityHint(item))}
        />
      );
    },
    minWidth: 300,
    maxWidth: 300,
  };
}

const getAnswerValidityHint = (item: TaskCompletionDetailsTableRow) =>
  item.answer?.validityHint;

function getResponseColumn(
  args: ResponseCellArgs,
): Column<TaskCompletionDetailsTableRow> | undefined {
  return {
    id: 'response',
    Header: args.t('response'),
    accessorFn: ({ item }: CellContentProps<TaskCompletionDetailsTableRow>) =>
      _get(item, 'answer.resp', [])?.[0]?.value ?? '-',
    Cell: (props: CellProps<TaskCompletionDetailsTableRow>) =>
      getResponseCellNode(props, args),
    minWidth: 300,
    maxWidth: 300,
  };
}

export default function getTaskCompletionDetailsTableColumns({
  t,
  readingDurationByDocumentationId,
  onDocumentClick,
  isViewLocked,
  isFullAdmin,
  onUpgradePlan,
}: {
  t: TFunction<'translation', undefined>;
  readingDurationByDocumentationId?: Map<string, number>;
  onDocumentClick?: (documentationId: string) => void;
  isViewLocked?: boolean;
  isFullAdmin?: boolean;
  onUpgradePlan?: () => void;
}) {
  const responseColumn = getResponseColumn({
    t,
    readingDurationByDocumentationId,
    onDocumentClick,
    isViewLocked,
    isFullAdmin,
    onUpgradePlan,
  });

  return _without(
    [
      getValidityHintColumn(getAnswerValidityHint),
      getQuestionColumn({ t, getValidityHint: getAnswerValidityHint }),
      responseColumn,
    ],
    undefined,
  ) as Column<TaskCompletionDetailsTableRow>[];
}
